import { ExpiringScope } from 'app/report/domain/expiring-lots';
import { getReportModuleRoutes, ReportPath } from 'app/report/routes';
import { GlobalContext } from 'context';
import { ExpiryAlerts as ExpiryAlertsData } from 'domain/notification';
import { MouseEvent as ReactMouseEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsyncBlock from 'shared/hooks/useAsyncBlock';
import useDispatchState from 'shared/hooks/useDispatchState';
import { NotificationApiActions } from 'store/notification-actions';
import ExpiryAlertSection from './ExpiryAlertSection';
import ExpiryAlertsStyled from './ExpiryAlerts.styled';

const {
  NavItem,
  BellButton,
  BellIcon,
  BadgeCount,
  Panel,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelBody,
  AllClear,
  PanelFooter,
  FooterLink,
} = ExpiryAlertsStyled;

const MAX_BADGE_COUNT = 99;
const ESCAPE_KEY = 'Escape';

const lotsLabel = (count: number): string => `${count} ${count === 1 ? 'lote' : 'lotes'}`;

const badgeText = (count: number): string => (count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count));

const ExpiryAlerts = (): ReactElement => {
  const [{ branchOfficeSelected }] = useDispatchState(GlobalContext);
  const branchOfficeId = branchOfficeSelected?.id;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);

  const loadAlerts = useCallback(
    async (): Promise<ExpiryAlertsData | null> => (branchOfficeId ? NotificationApiActions.getExpiryAlerts() : null),
    [branchOfficeId],
  );
  const { data: alerts, retry: refreshAlerts } = useAsyncBlock(loadAlerts, { keepPreviousData: true });

  const expiredCount = alerts?.expired.count ?? 0;
  const pendingCount = expiredCount + (alerts?.expiringSoon.count ?? 0);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === ESCAPE_KEY) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const togglePanel = (event: ReactMouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    if (!isOpen) refreshAlerts();
    setIsOpen(!isOpen);
  };

  const openReport = (scope?: ExpiringScope): void => {
    const reportPath = getReportModuleRoutes(ReportPath.ProductExpiration);
    navigate(scope ? `${reportPath}?scope=${scope}` : reportPath);
    setIsOpen(false);
  };

  return (
    <NavItem ref={containerRef} className="nav-item">
      <BellButton
        href="#"
        role="button"
        className="info-number"
        onClick={togglePanel}
        aria-expanded={isOpen}
        aria-label={pendingCount > 0 ? `Vencimientos: ${lotsLabel(pendingCount)} por atender` : 'Vencimientos: todo en orden'}
        title="Vencimientos"
      >
        <BellIcon className="fa fa-bell-o" aria-hidden="true" />
        {pendingCount > 0 && <BadgeCount $hasExpired={expiredCount > 0}>{badgeText(pendingCount)}</BadgeCount>}
      </BellButton>
      {isOpen && (
        <Panel role="dialog" aria-label="Vencimientos">
          <PanelHeader>
            <PanelTitle>Vencimientos</PanelTitle>
            <CloseButton type="button" onClick={() => setIsOpen(false)} aria-label="Cerrar">
              ×
            </CloseButton>
          </PanelHeader>
          <PanelBody>
            {alerts && pendingCount === 0 && (
              <AllClear role="status">
                <i className="fa fa-check-circle" aria-hidden="true"></i>
                No hay lotes vencidos ni que venzan en los próximos {alerts.expiringSoonDays} días.
              </AllClear>
            )}
            {alerts && (
              <>
                <ExpiryAlertSection
                  title="Vender o devolver pronto"
                  hint={`Vencen en los próximos ${alerts.expiringSoonDays} días`}
                  tone="warning"
                  group={alerts.expiringSoon}
                  moreLabel={`Ver los ${alerts.expiringSoon.count} lotes por vencer`}
                  onShowAll={() => openReport(ExpiringScope.Next30Days)}
                />
                <ExpiryAlertSection
                  title="Retirar de la venta"
                  hint="Ya vencieron: no se pueden vender"
                  tone="danger"
                  group={alerts.expired}
                  moreLabel={`Ver los ${alerts.expired.count} lotes vencidos`}
                  onShowAll={() => openReport(ExpiringScope.Expired)}
                />
              </>
            )}
          </PanelBody>
          <PanelFooter>
            <FooterLink type="button" onClick={() => openReport()}>
              Abrir Control de vencimientos <i className="fa fa-arrow-right" aria-hidden="true"></i>
            </FooterLink>
          </PanelFooter>
        </Panel>
      )}
    </NavItem>
  );
};

export default ExpiryAlerts;
